/**
 * MK Custom - B2C product order limits
 *
 * Enforces product.metafields.custom.order_limit in the theme across:
 * - desktop and mobile PDP buy boxes
 * - cart drawer quantity changes
 * - full cart quantity changes
 * - theme-rendered checkout buttons
 *
 * This is storefront enforcement. Shopify checkout itself cannot read this
 * metafield without a Shopify Function/app, so direct checkout URLs remain
 * outside the theme's control.
 */
(function () {
  'use strict';

  if (window.mkcustomOrderLimitControllerActive) return;
  window.mkcustomOrderLimitControllerActive = true;

  const rootRoute = window.Shopify?.routes?.root || '/';
  const cartUrl = `${rootRoute}cart.js`;
  const cartAddUrl = `${rootRoute}cart/add.js`;
  const cartUpdateUrl = `${rootRoute}cart/update.js`;
  const BUY_BOX_SELECTOR = '[data-mkcustom-product-id][data-mkcustom-order-limit]';
  const CART_ROW_SELECTOR = '.cart-item[data-product-id][data-mkcustom-order-limit]';
  const productNotices = new Map();
  let reconciliationPromise = null;
  let reconciliationTimer = null;
  let correctionInProgress = false;
  let activeCorrections = [];

  function toPositiveInteger(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
  }

  function getQuantity(value, fallback = 1) {
    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
  }

  async function getCart() {
    const response = await fetch(cartUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    });

    if (!response.ok) {
      throw new Error('Unable to check the cart. Please try again.');
    }

    return response.json();
  }

  function getProductQuantity(cart, productId) {
    return cart.items.reduce((total, item) => {
      return Number(item.product_id) === productId
        ? total + Number(item.quantity || 0)
        : total;
    }, 0);
  }

  function showWarning(element, heading, message) {
    if (!element) return;

    element.replaceChildren();

    const strong = document.createElement('strong');
    const span = document.createElement('span');

    strong.textContent = heading;
    span.textContent = message;

    element.append(strong, span);
    element.hidden = false;
    element.dataset.mkcustomOrderLimitMessage = 'true';
  }

  function clearWarning(element) {
    if (!element || element.dataset.mkcustomOrderLimitMessage !== 'true') return;

    element.replaceChildren();
    element.hidden = true;
    delete element.dataset.mkcustomOrderLimitMessage;
  }

  function setLimitDisabled(element, disabled) {
    if (!element) return;

    if (disabled) {
      if (!element.disabled) {
        element.disabled = true;
        element.dataset.mkcustomLimitDisabled = 'true';
      }
      return;
    }

    if (element.dataset.mkcustomLimitDisabled === 'true') {
      element.disabled = false;
      delete element.dataset.mkcustomLimitDisabled;
    }
  }

  function getBuyBoxConfiguration(form) {
    const buyBox = form.closest(BUY_BOX_SELECTOR);
    if (!buyBox) return null;

    return {
      buyBox,
      productId: Number(buyBox.dataset.mkcustomProductId),
      limit: toPositiveInteger(buyBox.dataset.mkcustomOrderLimit),
      quantityInput: form.querySelector('input[name="quantity"]'),
      variantInput: form.querySelector('input[name="id"]'),
      addButton: form.querySelector('button[name="add"]'),
      warning: form.querySelector('[data-buy-box-limit-warning]')
    };
  }

  function syncBuyBox(buyBox, cart) {
    const productId = Number(buyBox.dataset.mkcustomProductId);
    const limit = toPositiveInteger(buyBox.dataset.mkcustomOrderLimit);
    if (!productId || !limit) return;

    const form = buyBox.querySelector('.custom-buy-box__form');
    if (!form) return;

    const quantityInput = form.querySelector('input[name="quantity"]');
    const addButton = form.querySelector('button[name="add"]');
    const plusButton = form.querySelector('.custom-buy-box__qty-btn[name="plus"]');
    const minusButton = form.querySelector('.custom-buy-box__qty-btn[name="minus"]');
    const warning = form.querySelector('[data-buy-box-limit-warning]');
    const currentQuantity = getProductQuantity(cart, productId);
    const remaining = Math.max(0, limit - currentQuantity);

    buyBox.dataset.mkcustomCartProductQuantity = String(currentQuantity);
    buyBox.dataset.mkcustomRemainingQuantity = String(remaining);

    if (quantityInput) {
      quantityInput.dataset.mkcustomRemainingQuantity = String(remaining);

      if (remaining > 0) {
        quantityInput.max = String(remaining);
        setLimitDisabled(quantityInput, false);
      } else {
        quantityInput.max = '1';
        quantityInput.value = '1';
        setLimitDisabled(quantityInput, true);
      }
    }

    setLimitDisabled(plusButton, remaining <= 0);
    setLimitDisabled(minusButton, remaining <= 0);
    setLimitDisabled(addButton, remaining <= 0);

    if (remaining <= 0) {
      showWarning(
        warning,
        'Quantity Limit Reached',
        `This product is limited to ${limit} per order, and ${currentQuantity} ${currentQuantity === 1 ? 'is' : 'are'} already in your cart.`
      );
    } else {
      clearWarning(warning);
    }
  }

  function syncAllBuyBoxes(cart) {
    document.querySelectorAll(BUY_BOX_SELECTOR).forEach((buyBox) => {
      syncBuyBox(buyBox, cart);
    });
  }

  async function refreshBuyBoxes() {
    if (!document.querySelector(BUY_BOX_SELECTOR)) return;

    try {
      syncAllBuyBoxes(await getCart());
    } catch (error) {
      console.error('[MK Custom order limit]', error);
    }
  }

  function getCartAnalysis(cartItems) {
    const products = new Map();

    cartItems.querySelectorAll(CART_ROW_SELECTOR).forEach((row) => {
      const productId = Number(row.dataset.productId);
      const limit = toPositiveInteger(row.dataset.mkcustomOrderLimit);
      const input = row.querySelector('input[name="updates[]"]');

      if (!productId || !limit || !input) return;

      const record = products.get(productId) || {
        productId,
        limit,
        quantity: 0,
        rows: []
      };

      record.limit = limit;
      record.quantity += getQuantity(input.value, 0);
      record.rows.push(row);
      products.set(productId, record);
    });

    const violations = Array.from(products.values()).filter((product) => {
      return product.quantity > product.limit;
    });

    return { products, violations };
  }

  function getCartContainer(cartItems) {
    return cartItems.closest('cart-drawer') || cartItems.closest('form#cart') || cartItems.parentElement;
  }

  function getLineError(row) {
    return row.querySelector('.cart-item__error');
  }

  function showCartLineError(row, limit, message = '') {
    const error = getLineError(row);
    if (!error) return;

    const nextMessage = message || `Limit ${limit} per order, including all variants.`;

    // MK Custom - Avoid repeated DOM mutations while cart observers are active.
    if (error.textContent !== nextMessage) error.textContent = nextMessage;
    error.hidden = false;
    error.dataset.mkcustomOrderLimitMessage = 'true';
  }

  function clearCartLineError(row) {
    const error = getLineError(row);
    if (!error || error.dataset.mkcustomOrderLimitMessage !== 'true') return;

    error.textContent = '';
    error.hidden = true;
    delete error.dataset.mkcustomOrderLimitMessage;
  }

  function clearCartError(container) {
    const error = container?.querySelector('#cart-errors');
    if (!error || error.dataset.mkcustomOrderLimitMessage !== 'true') return;

    error.textContent = '';
    error.hidden = true;
    delete error.dataset.mkcustomOrderLimitMessage;
  }

  function setCheckoutBlocked(container, blocked) {
    if (!container) return;

    container.querySelectorAll('button[name="checkout"]').forEach((button) => {
      setLimitDisabled(button, blocked);
    });

    container.querySelectorAll('.dynamic-cart-btns').forEach((buttons) => {
      if (blocked) {
        if (!buttons.hidden) {
          buttons.hidden = true;
          buttons.dataset.mkcustomLimitHidden = 'true';
        }
      } else if (buttons.dataset.mkcustomLimitHidden === 'true') {
        buttons.hidden = false;
        delete buttons.dataset.mkcustomLimitHidden;
      }
    });
  }

  function syncCartSurface(cartItems) {
    const analysis = getCartAnalysis(cartItems);
    const container = getCartContainer(cartItems);

    analysis.products.forEach((product) => {
      let notice = productNotices.get(product.productId);

      // MK Custom - Clear an old limit notice after the customer reduces the
      // combined product quantity below the maximum.
      if (notice && product.quantity < product.limit) {
        productNotices.delete(product.productId);
        notice = '';
      }

      product.rows.forEach((row, rowIndex) => {
        const input = row.querySelector('input[name="updates[]"]');
        const lineQuantity = input ? getQuantity(input.value, 0) : 0;
        const otherQuantity = product.quantity - lineQuantity;
        const lineMaximum = Math.max(0, product.limit - otherQuantity);

        if (input) {
          // MK Custom - Keep the cart + button clickable so an over-limit
          // attempt can show a useful message instead of failing silently.
          input.dataset.mkcustomOrderLimit = String(product.limit);
          input.dataset.mkcustomLineMaximum = String(lineMaximum);

          if (product.quantity <= product.limit) {
            input.dataset.mkcustomLastValidQuantity = String(lineQuantity);
          }
        }

        if (rowIndex === 0 && product.quantity > product.limit) {
          showCartLineError(row, product.limit);
        } else if (rowIndex === 0 && notice) {
          showCartLineError(row, product.limit, notice);
        } else {
          clearCartLineError(row);
        }
      });
    });

    const blocked = correctionInProgress || analysis.violations.length > 0;
    setCheckoutBlocked(container, blocked);

    // MK Custom - Product limit feedback belongs only with the affected line.
    // Clear any older duplicate page-level message from the previous version.
    clearCartError(container);

    return !blocked;
  }

  function syncAllCartSurfaces() {
    document.querySelectorAll('cart-items').forEach((cartItems) => {
      syncCartSurface(cartItems);
    });
  }

  function scheduleCartSync() {
    [0, 250, 750, 1500].forEach((delay) => {
      window.setTimeout(syncAllCartSurfaces, delay);
    });
  }

  function getRenderedProductLimits() {
    const limits = new Map();

    document.querySelectorAll(CART_ROW_SELECTOR).forEach((row) => {
      const productId = Number(row.dataset.productId);
      const limit = toPositiveInteger(row.dataset.mkcustomOrderLimit);

      if (productId && limit) limits.set(productId, limit);
    });

    return limits;
  }

  async function refreshCorrectedCartSurfaces() {
    const fullCartItems = document.querySelector('form#cart cart-items');

    // MK Custom - Refresh the full cart directly when present.
    if (typeof fullCartItems?.refresh === 'function') {
      await fullCartItems.refresh();
    }

    // MK Custom - Use the theme's native drawer refresh so its recommendations,
    // financing, totals, and cart count remain synchronized.
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
      bubbles: true,
      detail: { source: 'mkcustom-order-limit' }
    }));
  }

  async function reconcileCartLimits() {
    if (reconciliationPromise) return reconciliationPromise;

    const limits = getRenderedProductLimits();
    if (!limits.size) return false;

    reconciliationPromise = (async () => {
      const cart = await getCart();
      const updates = {};
      const adjustedProducts = [];

      limits.forEach((limit, productId) => {
        const productItems = cart.items.filter((item) => {
          return Number(item.product_id) === productId;
        });
        const totalQuantity = productItems.reduce((total, item) => {
          return total + Number(item.quantity || 0);
        }, 0);

        if (totalQuantity <= limit) return;

        let excess = totalQuantity - limit;

        // MK Custom - Preserve existing cart lines and trim only the excess.
        // Line-item keys safely support variants and duplicate property lines.
        for (let index = productItems.length - 1; index >= 0 && excess > 0; index -= 1) {
          const item = productItems[index];
          const currentQuantity = Number(item.quantity || 0);
          const reduction = Math.min(currentQuantity, excess);

          updates[item.key] = currentQuantity - reduction;
          excess -= reduction;
        }

        adjustedProducts.push({ productId, limit });
      });

      if (!adjustedProducts.length) {
        correctionInProgress = false;
        activeCorrections = [];
        syncAllCartSurfaces();
        return false;
      }

      // Block theme checkout controls while the invalid external addition is
      // being corrected, including additions made by Fast Simon.
      correctionInProgress = true;
      activeCorrections = adjustedProducts;
      adjustedProducts.forEach(({ productId, limit }) => {
        productNotices.set(
          productId,
          `Quantity exceeded the limit and is being adjusted to ${limit}.`
        );
      });
      syncAllCartSurfaces();

      const response = await fetch(cartUpdateUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ updates })
      });

      const correctedCart = await response.json().catch(() => ({}));

      if (!response.ok || correctedCart.status) {
        throw new Error(
          correctedCart.description ||
          correctedCart.message ||
          'Unable to correct the product quantity.'
        );
      }

      adjustedProducts.forEach(({ productId, limit }) => {
        productNotices.set(
          productId,
          `Quantity adjusted to ${limit}. This product is limited to ${limit} per order.`
        );
      });

      correctionInProgress = false;
      activeCorrections = [];
      syncAllBuyBoxes(correctedCart);
      await refreshCorrectedCartSurfaces();
      scheduleCartSync();
      return true;
    })().catch((error) => {
      // MK Custom - Keep checkout blocked if Shopify could not correct an
      // invalid external addition and give the customer a useful next step.
      activeCorrections.forEach(({ productId, limit }) => {
        productNotices.set(
          productId,
          `This product is limited to ${limit} per order. Refresh the cart and try again.`
        );
      });
      syncAllCartSurfaces();
      console.error('[MK Custom order limit]', error);
      return false;
    }).finally(() => {
      reconciliationPromise = null;
    });

    return reconciliationPromise;
  }

  function scheduleReconciliation(delay = 100) {
    window.clearTimeout(reconciliationTimer);
    reconciliationTimer = window.setTimeout(reconcileCartLimits, delay);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest(
      'cart-items quantity-input button[name="plus"]'
    );
    if (!button) return;

    const cartItems = button.closest('cart-items');
    const row = button.closest(CART_ROW_SELECTOR);
    const quantityInput = button.closest('quantity-input');
    const input = quantityInput?.querySelector('input[name="updates[]"]');
    if (!cartItems || !row || !input) return;

    const productId = Number(row.dataset.productId);
    const limit = toPositiveInteger(row.dataset.mkcustomOrderLimit);
    if (!productId || !limit) return;

    let otherQuantity = 0;

    cartItems.querySelectorAll(CART_ROW_SELECTOR).forEach((candidate) => {
      if (candidate === row || Number(candidate.dataset.productId) !== productId) return;

      const candidateInput = candidate.querySelector('input[name="updates[]"]');
      otherQuantity += getQuantity(candidateInput?.value, 0);
    });

    const requestedQuantity = getQuantity(input.value, 0) + 1;
    if (otherQuantity + requestedQuantity <= limit) return;

    // MK Custom - Stop the quantity component before it increments or sends
    // a cart request, then explain the product-level limit to the customer.
    event.preventDefault();
    event.stopImmediatePropagation();

    const message = `Limit ${limit} per order.`;
    productNotices.set(productId, message);
    showCartLineError(row, limit, message);
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.custom-buy-box__qty-btn');
    if (!button) return;

    const form = button.closest('.custom-buy-box__form');
    if (!form) return;

    const configuration = getBuyBoxConfiguration(form);
    if (!configuration) return;

    const input = configuration.quantityInput;
    if (!input || input.disabled) return;

    event.preventDefault();

    const current = getQuantity(input.value);
    const remaining = toPositiveInteger(input.dataset.mkcustomRemainingQuantity) || configuration.limit;
    const isPlus = button.name === 'plus';
    const next = isPlus ? current + 1 : Math.max(1, current - 1);

    if (configuration.limit && isPlus && next > remaining) {
      showWarning(
        configuration.warning,
        'Order Limit',
        remaining > 0
          ? `You can add ${remaining} more of this product to this order.`
          : `This product is limited to ${configuration.limit} per order.`
      );
      return;
    }

    input.value = String(next);
    clearWarning(configuration.warning);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }, true);

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('.custom-buy-box__form');
    if (!form) return;

    if (
      event.submitter &&
      !event.submitter.matches(
        'button[name="add"], input[name="add"], [data-native-pre-order-btn]'
      )
    ) {
      return;
    }

    const configuration = getBuyBoxConfiguration(form);
    if (!configuration || !configuration.variantInput) return;

    event.preventDefault();

    if (form.dataset.mkcustomSubmitting === 'true') return;
    form.dataset.mkcustomSubmitting = 'true';

    const requestedQuantity = getQuantity(configuration.quantityInput?.value);
    const addButton = configuration.addButton;
    const buttonWasDisabled = Boolean(addButton?.disabled);

    if (addButton) {
      addButton.disabled = true;
      addButton.setAttribute('aria-busy', 'true');
    }

    try {
      const currentCart = await getCart();
      const currentProductQuantity = getProductQuantity(currentCart, configuration.productId);
      const remaining = Math.max(0, configuration.limit - currentProductQuantity);

      if (configuration.limit && requestedQuantity > remaining) {
        showWarning(
          configuration.warning,
          remaining > 0 ? 'Reduce Quantity' : 'Quantity Limit Reached',
          remaining > 0
            ? `This product is limited to ${configuration.limit} per order. You can add ${remaining} more.`
            : `This product is limited to ${configuration.limit} per order, and ${currentProductQuantity} ${currentProductQuantity === 1 ? 'is' : 'are'} already in your cart.`
        );

        syncAllBuyBoxes(currentCart);
        return;
      }

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: new FormData(form)
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok || responseData.status) {
        throw new Error(
          responseData.description ||
          responseData.message ||
          'This item could not be added to your cart.'
        );
      }

      const updatedCart = await getCart();

      clearWarning(configuration.warning);
      if (configuration.quantityInput) configuration.quantityInput.value = '1';
      syncAllBuyBoxes(updatedCart);

      document.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: {
          cart: updatedCart,
          variantId: Number(configuration.variantInput.value),
          quantity: requestedQuantity
        }
      }));

      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
        bubbles: true
      }));

      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
        bubbles: true
      }));

      scheduleCartSync();
    } catch (error) {
      showWarning(
        configuration.warning,
        'Unable to Add to Cart',
        error.message || 'Please try again.'
      );
      console.error('[MK Custom order limit]', error);
    } finally {
      form.dataset.mkcustomSubmitting = 'false';

      if (addButton) {
        addButton.removeAttribute('aria-busy');

        if (addButton.dataset.mkcustomLimitDisabled !== 'true') {
          addButton.disabled = buttonWasDisabled;
        }
      }
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('cart-items input[name="updates[]"]');
    if (!input) return;

    const cartItems = input.closest('cart-items');
    const row = input.closest(CART_ROW_SELECTOR);
    if (!cartItems || !row) return;

    const productId = Number(row.dataset.productId);
    const limit = toPositiveInteger(row.dataset.mkcustomOrderLimit);
    if (!productId || !limit) return;

    let otherQuantity = 0;

    cartItems.querySelectorAll(CART_ROW_SELECTOR).forEach((candidate) => {
      if (candidate === row || Number(candidate.dataset.productId) !== productId) return;

      const candidateInput = candidate.querySelector('input[name="updates[]"]');
      otherQuantity += getQuantity(candidateInput?.value, 0);
    });

    const requestedQuantity = getQuantity(input.value, 0);

    if (otherQuantity + requestedQuantity <= limit) {
      productNotices.delete(productId);
      clearCartLineError(row);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const originalQuantity = getQuantity(
      input.dataset.mkcustomLastValidQuantity,
      getQuantity(input.dataset.initialValue)
    );
    input.value = String(originalQuantity);

    const quantityInput = input.closest('quantity-input');
    if (quantityInput) quantityInput.currentQty = originalQuantity;

    productNotices.set(
      productId,
      `Limit ${limit} per order.`
    );
    syncCartSurface(cartItems);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.id !== 'cart' && form.id !== 'cart-drawer-form') return;

    const cartItems = form.querySelector('cart-items');
    if (!cartItems || syncCartSurface(cartItems)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener('on:cart:add', (event) => {
    if (event.detail?.cart) syncAllBuyBoxes(event.detail.cart);
    scheduleCartSync();
    scheduleReconciliation(50);
  });

  document.addEventListener('on:line-item:change', () => {
    scheduleCartSync();
    window.setTimeout(refreshBuyBoxes, 150);
    scheduleReconciliation(150);
  });

  document.addEventListener('dispatch:cart-drawer:refresh', () => {
    scheduleCartSync();
    window.setTimeout(refreshBuyBoxes, 150);
    scheduleReconciliation(250);
  });

  document.addEventListener('shopify:section:load', () => {
    scheduleCartSync();
    refreshBuyBoxes();
    scheduleReconciliation(150);
  });

  window.addEventListener('pageshow', () => {
    scheduleCartSync();
    refreshBuyBoxes();
    scheduleReconciliation(100);
  });

  // MK Custom - Detect third-party cart renders (including Fast Simon) without
  // modifying or depending on the third-party implementation.
  const cartObserver = new MutationObserver((mutations) => {
    const cartChanged = mutations.some((mutation) => {
      const target = mutation.target instanceof Element ? mutation.target : null;
      if (target?.closest('cart-items')) return true;

      return Array.from(mutation.addedNodes).some((node) => {
        if (!(node instanceof Element)) return false;
        return node.matches('cart-items, .cart-item') || Boolean(node.querySelector('cart-items, .cart-item'));
      });
    });

    if (!cartChanged) return;

    syncAllCartSurfaces();
    scheduleReconciliation(100);
  });

  if (document.body) {
    cartObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      syncAllCartSurfaces();
      refreshBuyBoxes();
      scheduleReconciliation(100);
    }, { once: true });
  } else {
    syncAllCartSurfaces();
    refreshBuyBoxes();
    scheduleReconciliation(100);
  }
})();
